import useSWR from 'swr';
import { KeyedMutator, SWRConfiguration } from 'swr/dist/types';

import { FetchError } from '../../classes/FetchError';
import { BaseApiResponseState } from '../../interfaces/BaseAPIResponseState';
import { fetcher } from './fetcher';

export interface DataState<Data> extends BaseApiResponseState<Data> {
  mutate: KeyedMutator<Data | undefined>;
}

export interface UseFetchArgs<Data, RawData = unknown> {
  // Allow passing a null url to support conditional fetching
  // https://swr.vercel.app/docs/conditional-fetching#conditional
  url: string | null;
  // We allow `any` here since most deserializer functions should specify the correct type
  // in their signatures.
  deserializer?: (data: RawData) => Data;
  // Pass custom SWR options
  // https://swr.vercel.app/docs/options#options
  swrConfig?: SWRConfiguration;
  // By default we redirect to the login page on any 401 response; set this to true to disable this
  skipRedirectOn401?: boolean;
  // While useSWR is only meant to be used for GET requests, we support non-mutating POST requests in special
  // cases, e.g. if the payload of the request is too big to fit into query params.
  dangerouslyUsePOSTWithPayload?: unknown;
  // Custom headers
  headers?: Record<string, string>;
}

/**
 * A wrapper around useSWR.
 *
 * https://swr.vercel.app/
 */
export function useFetch<Data, RawData = unknown>({
  url,
  skipRedirectOn401,
  dangerouslyUsePOSTWithPayload,
  headers,
  swrConfig,
  deserializer,
}: UseFetchArgs<Data, RawData>): DataState<Data> {
  // If url is null - prevent the request by passing null key
  // as described here https://swr.vercel.app/docs/conditional-fetching
  const key =
    url !== null
      ? [
          // Because "Parameters<typeof fetcher>" does not support generics,
          // we must manually keep this array matching `fetcher` args above.
          url,
          skipRedirectOn401,
          dangerouslyUsePOSTWithPayload &&
            JSON.stringify(dangerouslyUsePOSTWithPayload),
          headers && JSON.stringify(headers),
          deserializer,
        ]
      : null;

  const { data, error, mutate } = useSWR<Data | undefined, FetchError>(
    key,
    fetcher,
    swrConfig,
  );

  return {
    data,
    error,
    isLoading: key !== null ? !data && !error : false,
    // Used for updating the local data immediately instead of waiting for a re-fetch
    // See https://swr.vercel.app/docs/mutation#bound-mutate
    mutate,
  };
}
